import Cookies from 'universal-cookie';

import { logRuntimeError } from 'Diagnostics/diagnostics';
import { Vehicle, VehicleCollection } from 'Models/index';
import type { SearchItemRawData } from 'Models/searchItemData';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import uiConfig from 'Models/uiConfig/uiConfig';
import type { VehicleData, VehicleExtra, VehicleValue } from 'Models/vehicle';

import keys from './userStorageKeys';

const cookies = new Cookies();
export { keys };

type QueryHistoryItem = string;
type ProductHistoryItem = SearchItemRawData & { url?: string };
type TrackedThemeId = string | number;
type VehicleRawData = VehicleData;

/**
 * Storage values are written through `JSON.stringify` and read back via
 * `JSON.parse`. The round-trip silently drops `undefined`, functions,
 * `Symbol`, and the prototype chain, and throws on circular references.
 *
 * We accept a permissive runtime-value union here (rather than a strict
 * recursive `JsonValue` lattice) because the existing `setItem` callers
 * pass server-shaped objects whose fields use `ServerValue`. The
 * write-time `safeStringify` helper logs to diagnostics if a value
 * cannot serialize, and the read-time `getItem` predicate validates
 * the parsed shape — together they cover the boundary at runtime even
 * when the static type is loose.
 */
type StorageValue = unknown;

// ---- history methods ----

export const loadQueriesHistory = (): QueryHistoryItem[] => {
  const serializedState = getItem(keys.queriesHistory, isStringArray);
  return serializedState ?? [];
};

export const loadProductsHistory = (): ProductHistoryItem[] => {
  const serializedState = getItem(keys.productsHistory, isProductHistoryItemArray);
  if (!serializedState) {
    return [];
  }
  const url = location.href;
  return serializedState.filter((record) => record.url !== url);
};

const productSize = 3;
export const addProductsHistory = (product: ProductHistoryItem): void => {
  const productsHistory = loadProductsHistory();
  if (productsHistory.length >= productSize) {
    productsHistory.pop();
  }
  const url = location.href;
  productsHistory.unshift({ ...product, url });
  setItem(keys.productsHistory, productsHistory);
};

// ---- compared items methods ----

export const loadCompared = (): SearchItemRawData[] => {
  const compared = getItem(keys.compared, isObjectArray);
  return compared ?? [];
};

// ---- garage & model methods ----

export function getVehicleData(key: string): unknown {
  const expires = fitmentSearchConfig.vehicleDataExpires
    ? new Date(Date.now() + fitmentSearchConfig.vehicleDataExpires * 60 * 1000)
    : null;

  return getItemRaw(key, !!fitmentSearchConfig.vehicleDataExpires, expires);
}

export function setVehicleData(key: string, value: StorageValue): void {
  if (!fitmentSearchConfig.doNotSaveSelectedVehicle) {
    const expires = fitmentSearchConfig.vehicleDataExpires
      ? new Date(Date.now() + fitmentSearchConfig.vehicleDataExpires * 60 * 1000)
      : null;

    setItem(key, value, !!fitmentSearchConfig.vehicleDataExpires, expires);
  }
}

export function removeVehicleData(key: string): void {
  removeItem(key, !!fitmentSearchConfig.vehicleDataExpires);
}

export function loadSelectedVehicle(): typeof Vehicle.null | Vehicle {
  const { doNotLoadSelectedVehicle } = fitmentSearchConfig;
  if (doNotLoadSelectedVehicle) {
    return Vehicle.null;
  }

  const data = getVehicleData(keys.vehicle);
  return isVehicleRawData(data) ? deserializeVehicle(data) : Vehicle.null;
}

export function loadGarageData(): VehicleCollection {
  const data = getVehicleData(keys.garageData);
  return isVehicleRawDataList(data) && data.length
    ? new VehicleCollection(data.map(deserializeVehicle))
    : VehicleCollection.empty;
}

export function getForceVehicleSelectionLater(): boolean | null {
  return getItem(keys.forceVehicleSelectionLater, isBoolean, true);
}

export function setForceVehicleSelectionLater(): void {
  const expires = new Date(Date.now() + fitmentSearchConfig.forceVehicleSelectionInterval * 60 * 1000);
  setItem(keys.forceVehicleSelectionLater, true, true, expires);
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isTrackedThemeId(value: unknown): value is TrackedThemeId {
  return typeof value === 'string' || typeof value === 'number';
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(isString);
}

function isObjectArray(value: unknown): value is SearchItemRawData[] {
  return Array.isArray(value) && value.every((v) => v !== null && typeof v === 'object' && !Array.isArray(v));
}

function isProductHistoryItemArray(value: unknown): value is ProductHistoryItem[] {
  return (
    Array.isArray(value) &&
    value.every((v) => {
      if (v === null || typeof v !== 'object' || Array.isArray(v)) {
        return false;
      }
      const { url } = v as { url?: unknown };
      return url === undefined || typeof url === 'string';
    })
  );
}

/**
 * `VehicleData` is `(VehicleValue | { vehicleExtra: VehicleExtra })[]`.
 * `deserializeVehicle` reads `entry.vehicleExtra` and falls back to a
 * `VehicleValue` shape for the rest, so the runtime check needs to
 * confirm we have an array of object-records — not a bare array of
 * arbitrary values that would crash the `Vehicle` constructor.
 */
function isVehicleRawData(value: unknown): value is VehicleRawData {
  return Array.isArray(value) && value.every((entry) => entry !== null && typeof entry === 'object');
}

function isVehicleRawDataList(value: unknown): value is VehicleRawData[] {
  return Array.isArray(value) && value.every(isVehicleRawData);
}

function deserializeVehicle(data: VehicleRawData): Vehicle {
  const vehicleExtraData =
    data.find(
      (entry): entry is { vehicleExtra: VehicleExtra } => 'vehicleExtra' in entry && !!entry.vehicleExtra,
    )?.vehicleExtra ?? {};
  const vehicleValues = data.filter((entry): entry is VehicleValue => !('vehicleExtra' in entry));

  // it's used for init, so we're creating vehicle with fitmentSearchConfig fields
  return new Vehicle(vehicleValues, fitmentSearchConfig.fields, vehicleExtraData);
}

// ---- customer methods ----

export function isLoggedInStateChanged(isLoggedInNow: boolean): boolean {
  const wasLoggedIn = getItem(keys.isLoggedIn, isBoolean);
  setItem(keys.isLoggedIn, isLoggedInNow);
  return wasLoggedIn !== isLoggedInNow;
}

// ---- view methods ----

export function loadView(): string | null {
  return getItem(keys.view, isString);
}

// ---- track methods ----

export function getTrackedTheme(): TrackedThemeId | null {
  return getItem(keys.trackedThemeId, isTrackedThemeId);
}

export function setTrackedTheme(id: TrackedThemeId): void {
  setItem(keys.trackedThemeId, id);
}

// ---- searchId methods ----

export function getSearchId(): string | null {
  return getItem(keys.searchId, isString);
}

export function setSearchId(searchId: string): void {
  setItem(keys.searchId, searchId);
}

// ---- common methods ----

let localStorageWarned = false;
const defaultCookieExpires = new Date(Date.now() + 5 * 365 * 24 * 60 * 60 * 1000); // 5 years long

function warnLocalStorage(e: unknown): void {
  if (!localStorageWarned) {
    console.warn(
      '[Convermax] localStorage unavailable — garage, vehicle selection, search history, and view preferences will not be persisted for this session.',
      e,
    );
    localStorageWarned = true;
  }
}

/**
 * Cookies surface every value as a string. The legacy server stack stores
 * the literal `'undefined'` for missing values, so we treat that as null
 * (rather than letting `JSON.parse('undefined')` throw).
 *
 * `doNotParse: true` keeps the universal-cookie library from JSON-decoding
 * for us — we control parsing in `getItemRaw` so a single bad value can
 * be reported via diagnostics instead of crashing every read.
 */
function cookieGet(key: string): string | null {
  const value = cookies.get(key, { doNotParse: true });
  if (typeof value === 'string' && value !== 'undefined') {
    return value;
  }
  return null;
}

function safeJsonParse(text: string, source: string): unknown {
  try {
    return JSON.parse(text);
  } catch (error) {
    logRuntimeError({
      source: `userStorage:${source}`,
      error,
      extra: { 'value.preview': text.length > 200 ? `${text.slice(0, 200)}…` : text },
    });
    return null;
  }
}

/**
 * Read the raw JSON-decoded value for a storage key. The result is
 * `unknown` because the underlying string is fundamentally untrusted —
 * callers either narrow with a predicate (via `getItem`) or use the
 * raw value directly when downstream code already validates the shape
 * (e.g. `Vehicle` deserialization).
 */
function getItemRaw(key: string, useCookie?: boolean, expires?: Date | null): unknown {
  if (uiConfig.cookies.use || useCookie) {
    const value = cookieGet(key);
    if (expires && value) {
      // Re-write to refresh the cookie's expiration window without parsing it.
      cookies.set(key, value, { expires: expires ?? defaultCookieExpires, path: '/' });
    }
    if (!value) {
      return null;
    }
    return safeJsonParse(value.replace(/\+/g, ' '), `cookie:${key}`);
  }
  try {
    const value = window.localStorage.getItem(key);
    return value ? safeJsonParse(value, `localStorage:${key}`) : null;
  } catch (e) {
    warnLocalStorage(e);
    return null;
  }
}

/**
 * Read a typed value from storage, returning `null` if the slot is empty,
 * unparseable, or fails the supplied `predicate`. Callers MUST pass a
 * predicate; the parsed value is `unknown` and casting it would defeat
 * the boundary check this module exists to provide.
 */
export function getItem<T>(
  key: string,
  predicate: (value: unknown) => value is T,
  useCookie?: boolean,
  expires?: Date | null,
): T | null {
  const raw = getItemRaw(key, useCookie, expires);
  if (raw === null) {
    return null;
  }
  if (predicate(raw)) {
    return raw;
  }
  logRuntimeError({
    source: `userStorage:get:${key}`,
    error: new Error(`storage value for key "${key}" did not match expected shape`),
  });
  return null;
}

/**
 * `JSON.stringify` returns `undefined` for unsupported root values
 * (functions, `Symbol`) and throws on circular references. We surface
 * both as a single `null` + diagnostic event so a malformed value
 * never silently corrupts a storage slot.
 */
function safeStringify(value: StorageValue, source: string): string | null {
  try {
    const result = JSON.stringify(value);
    if (result === undefined) {
      logRuntimeError({
        source: `userStorage:set:${source}`,
        error: new Error('value is not JSON-serializable (function, symbol, or undefined root)'),
      });
      return null;
    }
    return result;
  } catch (error) {
    logRuntimeError({
      source: `userStorage:set:${source}`,
      error,
    });
    return null;
  }
}

export function setItem(
  key: string,
  value: StorageValue,
  useCookie?: boolean | Date | null,
  expires?: Date | null,
): void {
  if (uiConfig.cookies.use || Boolean(useCookie && !(useCookie instanceof Date))) {
    cookies.set(key, value, { expires: expires ?? defaultCookieExpires, path: '/' });
  } else {
    const serialized = safeStringify(value, key);
    if (serialized === null) {
      return;
    }
    try {
      window.localStorage.setItem(key, serialized);
    } catch (e) {
      warnLocalStorage(e);
    }
  }
}

export function removeItem(key: string, useCookie?: boolean): void {
  if (uiConfig.cookies.use || useCookie) {
    cookies.set(key, '', { path: '/', expires: new Date() });
  } else {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      warnLocalStorage(e);
    }
  }
}
